/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import Slider from '@mui/material/Slider';

import { Checkbox } from 'carbon-react/lib/components/checkbox';

import { CARD_TYPES, STROKE_BORDER_POSITIONS, TableStyle, TABLE_FONT_WEIGHTS, WidgetData, RowTypes } from '../../types';
import { useReportBuilderContext } from '../../report-builder.context';
import { TableRowThemeCustomization } from './table-row-theme-customization';
import {
  TABLE_COLUMN_WIDTH,
  TABLE_FIRST_COLUMN_WIDTH,
  defaultFillColor,
  newDefaultRowStyles,
  varianceBG
} from '../../constants';

import styles from './theme-customization.scss';

export const defaultHeaderRowStyle: TableStyle = {
  fontWeight: TABLE_FONT_WEIGHTS.BOLD,
  fontSize: 10,
  color: '#000000',
  strokeColor: '#000000',
  strokeWidth: 1,
  strokePosition: STROKE_BORDER_POSITIONS.NONE
};
export const defaultNormalRowStyle: TableStyle = {
  fontWeight: TABLE_FONT_WEIGHTS.NORMAL,
  fontSize: 10,
  color: '#000000',
  strokeColor: '#000000',
  strokeWidth: 1,
  strokePosition: STROKE_BORDER_POSITIONS.NONE
};

const allRowTypes: RowTypes[] = [
  'headerRow',
  'forecastsRow',
  'normalRow',
  'categoriesRow',
  'groupsRow',
  'accountsRow',
  'assumptionsRow',
  'formulasRow',
  'driverGroupsRow',
  'driversRow'
];

export const TableThemeCustomization = () => {
  const intl = useIntl();
  const { currentCard, getSelectedDatasets, setRowsStyle, setWidgetDataByKey } = useReportBuilderContext();
  const selectedForecasts = getSelectedDatasets();
  const widgetOptions: WidgetData | undefined = currentCard?.[CARD_TYPES.WIDGET];
  const [firstColumnWidth, setFirstColumnWidth] = useState(
    widgetOptions?.tableFirstColumnWidth || TABLE_FIRST_COLUMN_WIDTH
  );
  const [columnWidth, setColumnWidth] = useState(widgetOptions?.tableColumnWidth || TABLE_COLUMN_WIDTH);
  const [zoom, setZoom] = useState(widgetOptions?.tableZoom || 1);
  const firstColTimerRef = useRef<number | null>(null);
  const colTimerRef = useRef<number | null>(null);
  const zoomTimerRef = useRef<number | null>(null);

  const rowTypeDisplayableNames = useMemo(
    () => ({
      headerRow: intl.formatMessage({ id: 'reporting.header', defaultMessage: 'Header' }),
      forecastsRow: intl.formatMessage({ id: 'reporting.forecasts', defaultMessage: 'Forecasts' }),
      normalRow: intl.formatMessage({ id: 'common.normal', defaultMessage: 'Normal' }),
      categoriesRow: intl.formatMessage({ id: 'common.category', defaultMessage: 'Category' }),
      groupsRow: intl.formatMessage({ id: 'common.group', defaultMessage: 'Group' }),
      accountsRow: intl.formatMessage({ id: 'common.account', defaultMessage: 'Account' }),
      assumptionsRow: intl.formatMessage({ id: 'common.prediction', defaultMessage: 'Prediction' }),
      formulasRow: intl.formatMessage({ id: 'common.formula', defaultMessage: 'Formula' }),
      driverGroupsRow: intl.formatMessage({ id: 'reporting.driver_group', defaultMessage: 'Driver group' }),
      driversRow: intl.formatMessage({ id: 'reporting.driver', defaultMessage: 'Driver' })
    }),
    [intl]
  );

  const currentCustomRowStyle = useMemo(() => {
    const stylesPerType: Record<RowTypes, TableStyle> = {} as Record<RowTypes, TableStyle>;
    allRowTypes.forEach(type => {
      const typeRowStyle = widgetOptions?.customRow?.find(e => e.type === type.replace('Row', '')) || {};
      stylesPerType[type] = {
        ...newDefaultRowStyles[type],
        ...typeRowStyle,
        ...(widgetOptions?.[type] || {})
      };
    });
    return stylesPerType;
  }, [widgetOptions]);

  const getTableRowName = (type: RowTypes) => {
    return intl.formatMessage(
      { id: 'reporting.row_type_rows', defaultMessage: '{rowType} rows' },
      { rowType: rowTypeDisplayableNames[type] }
    );
  };

  const updateStyle = (type: RowTypes, style: Partial<TableStyle>) => {
    const oldStyle = currentCustomRowStyle[type];
    const newStyle = { ...style };
    // if we change fill, we also want to update fillPast and fillFuture and their even countreparts if they have default values
    if (style.fill) {
      if (!oldStyle.fillPast || oldStyle.fillPast === defaultFillColor || oldStyle.fillPast === oldStyle.fill) {
        newStyle.fillPast = style.fill;
      }
      if (!oldStyle.fillFuture || oldStyle.fillFuture === defaultFillColor || oldStyle.fillFuture === oldStyle.fill) {
        newStyle.fillFuture = style.fill;
      }
      if (type !== 'headerRow' && type !== 'forecastsRow') {
        if (!oldStyle.fillEven || oldStyle.fillEven === defaultFillColor || oldStyle.fillEven === oldStyle.fill) {
          newStyle.fillEven = style.fill;
        }
        if (
          !oldStyle.fillPastEven ||
          oldStyle.fillPastEven === defaultFillColor ||
          oldStyle.fillPastEven === oldStyle.fill
        ) {
          newStyle.fillPastEven = style.fill;
        }
        if (
          !oldStyle.fillFutureEven ||
          oldStyle.fillFutureEven === defaultFillColor ||
          oldStyle.fillFutureEven === oldStyle.fill
        ) {
          newStyle.fillFutureEven = style.fill;
        }
      }
    }
    if (style.fillEven) {
      if (
        !oldStyle.fillPastEven ||
        oldStyle.fillPastEven === defaultFillColor ||
        oldStyle.fillPastEven === oldStyle.fillEven
      ) {
        newStyle.fillPastEven = style.fillEven;
      }
      if (
        !oldStyle.fillFutureEven ||
        oldStyle.fillFutureEven === defaultFillColor ||
        oldStyle.fillFutureEven === oldStyle.fillEven
      ) {
        newStyle.fillFutureEven = style.fillEven;
      }
    }
    if (
      style.fillPast &&
      type !== 'headerRow' &&
      type !== 'forecastsRow' &&
      (!oldStyle.fillPastEven ||
        oldStyle.fillPastEven === defaultFillColor ||
        oldStyle.fillPastEven === oldStyle.fillPast)
    ) {
      newStyle.fillPastEven = style.fillPast;
    }
    if (
      style.fillFuture &&
      type !== 'headerRow' &&
      type !== 'forecastsRow' &&
      (!oldStyle.fillFutureEven ||
        oldStyle.fillFutureEven === defaultFillColor ||
        oldStyle.fillFutureEven === oldStyle.fillFuture)
    ) {
      newStyle.fillFutureEven = style.fillFuture;
    }
    if (
      style.fillVariance &&
      type !== 'headerRow' &&
      type !== 'forecastsRow' &&
      (!oldStyle.fillVarianceEven ||
        oldStyle.fillVarianceEven === varianceBG ||
        oldStyle.fillVarianceEven === oldStyle.fillVariance)
    ) {
      newStyle.fillVarianceEven = style.fillVariance;
    }
    const styles: { type: RowTypes; style: TableStyle }[] = [{ type, style: { ...oldStyle, ...newStyle } }];
    // if we change normalRow style, we should update all types if they have default values
    if (type === 'normalRow') {
      ([
        'categoriesRow',
        'groupsRow',
        'accountsRow',
        'assumptionsRow',
        'formulasRow',
        'driversRow',
        'driverGroupsRow'
      ] as RowTypes[]).forEach(t => {
        const typeStyle = currentCustomRowStyle[t];
        const newTypeStyle: Record<string, string | number | undefined> = {};
        Object.keys(newStyle).forEach(key => {
          const k = key as keyof TableStyle;
          if (
            !typeStyle[k] ||
            typeStyle[k] === defaultNormalRowStyle[k] ||
            typeStyle[k] === oldStyle[k] ||
            (k.includes('fill') && typeStyle[k] === defaultFillColor)
          ) {
            newTypeStyle[k] = newStyle[k];
          }
        });
        if (Object.keys(newTypeStyle).length) {
          styles.push({ type: t, style: { ...typeStyle, ...newTypeStyle } });
        }
      });
    }
    setRowsStyle(styles);
  };

  const handleShouldPaginateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setWidgetDataByKey('tableShouldPaginate', event.target.checked);
  };

  useEffect(() => {
    if (firstColTimerRef.current) {
      clearTimeout(firstColTimerRef.current);
      firstColTimerRef.current = null;
    }
    firstColTimerRef.current = (setTimeout(() => {
      if (firstColumnWidth !== (widgetOptions?.tableFirstColumnWidth || TABLE_FIRST_COLUMN_WIDTH)) {
        setWidgetDataByKey('tableFirstColumnWidth', firstColumnWidth);
      }
    }, 50) as unknown) as number;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstColumnWidth, widgetOptions?.tableFirstColumnWidth]);

  useEffect(() => {
    if (colTimerRef.current) {
      clearTimeout(colTimerRef.current);
      colTimerRef.current = null;
    }
    colTimerRef.current = (setTimeout(() => {
      if (columnWidth !== (widgetOptions?.tableColumnWidth || TABLE_COLUMN_WIDTH)) {
        setWidgetDataByKey('tableColumnWidth', columnWidth);
      }
    }, 50) as unknown) as number;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnWidth, widgetOptions?.tableColumnWidth]);

  useEffect(() => {
    if (zoomTimerRef.current) {
      clearTimeout(zoomTimerRef.current);
      zoomTimerRef.current = null;
    }
    zoomTimerRef.current = (setTimeout(() => {
      if (zoom !== (widgetOptions?.tableZoom || 1)) {
        setWidgetDataByKey('tableZoom', zoom);
      }
    }, 50) as unknown) as number;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, widgetOptions?.tableZoom]);

  const handleFirstColumnWidthChange = (_event: Event, value: number | number[]) => {
    setFirstColumnWidth(value as number);
  };

  const handleColumnWidthChange = (_event: Event, value: number | number[]) => {
    setColumnWidth(value as number);
  };

  const handleZoomChange = (_event: Event, value: number | number[]) => {
    setZoom(value as number);
  };

  return (
    <div className={clsx(styles.tableThemeContainer)}>
      <h3>
        <FormattedMessage id="reporting.wrap_table_columns" defaultMessage="Wrap table columns" />
      </h3>
      <div className={styles.checkboxLabel}>
        <Checkbox
          size="small"
          checked={widgetOptions?.tableShouldPaginate || false}
          onChange={handleShouldPaginateChange}
          label={<FormattedMessage id="reporting.wrap_table_columns" defaultMessage="Wrap table columns" />}
        />
      </div>
      <h3 id="first-column-width-slider-label">
        <FormattedMessage id="reporting.first_column_width" defaultMessage="1st column width" />
      </h3>
      <div className={clsx(styles.sliderContainer)}>
        <Slider
          value={firstColumnWidth}
          min={60}
          step={5}
          max={300}
          onChange={handleFirstColumnWidthChange}
          valueLabelDisplay="auto"
          aria-labelledby="first-column-width-slider-label"
          classes={{
            root: styles.slider
          }}
        />
      </div>
      <h3 id="data-column-width-slider">
        <FormattedMessage id="reporting.data_column_width" defaultMessage="Data column width" />
      </h3>
      <div className={clsx(styles.sliderContainer)}>
        <Slider
          value={columnWidth}
          min={40}
          step={5}
          max={200}
          onChange={handleColumnWidthChange}
          valueLabelDisplay="auto"
          aria-labelledby="data-column-width-slider"
          classes={{
            root: styles.slider
          }}
        />
      </div>
      {widgetOptions?.tableShouldPaginate && (
        <>
          <h3>
            <FormattedMessage id="reporting.table zoom" defaultMessage="Table zoom" />
          </h3>
          <div className={clsx(styles.sliderContainer)}>
            <Slider
              value={zoom}
              min={0.5}
              step={0.05}
              max={1.5}
              onChange={handleZoomChange}
              valueLabelDisplay="auto"
              aria-labelledby="zoom-slider"
              classes={{
                root: styles.slider
              }}
              marks={[{ value: 1 }]}
            />
          </div>
        </>
      )}

      <TableRowThemeCustomization
        rowName={intl.formatMessage({ id: 'reporting.header_row', defaultMessage: 'Header row' })}
        type="headerRow"
        currentStyle={currentCustomRowStyle.headerRow}
        updateStyle={updateStyle}
        hideEvenFillOptions
      />
      {selectedForecasts.length > 1 && (
        <TableRowThemeCustomization
          rowName={intl.formatMessage({ id: 'reporting.forecasts_header_row', defaultMessage: 'Forecasts header row' })}
          type="forecastsRow"
          currentStyle={currentCustomRowStyle.forecastsRow}
          updateStyle={updateStyle}
          hideEvenFillOptions
        />
      )}
      <TableRowThemeCustomization
        rowName={intl.formatMessage({ id: 'reporting.rows', defaultMessage: 'Rows' })}
        type="normalRow"
        currentStyle={currentCustomRowStyle.normalRow}
        updateStyle={updateStyle}
      />

      {([
        'categoriesRow',
        'groupsRow',
        'accountsRow',
        'assumptionsRow',
        'formulasRow',
        'driverGroupsRow',
        'driversRow'
      ] as RowTypes[]).map(type => (
        <TableRowThemeCustomization
          key={type}
          rowName={getTableRowName(type)}
          type={type}
          currentStyle={currentCustomRowStyle[type]}
          updateStyle={updateStyle}
        />
      ))}
    </div>
  );
};
